import { EditorContent, Extension, useEditor } from "@tiptap/react";
import React, { forwardRef } from "react";

import styled from "styled-components/macro";

import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import Text from "@tiptap/extension-text";
import { AnimatePresence, motion } from "framer-motion";

// custom extension to prevent new lines on enter
const DisableEnter = Extension.create({
  addKeyboardShortcuts() {
    return {
      Enter: () => true,
    };
  },
});

const Tiptap = ({
  handleUpdate,
  disabled = false,
  initialContent = "",
  autoFocus = false,
  newLineOnEnter = false,
  onBlur,
}) => {
  /**
   * UPDATE: 2021-03-02
   * I seems to have fixed this by adding initialContent prop to TiptapCE
   * and using that to pass into this component. When it updates, it will update the editor.
   * I'm leaving these notes in case we run into problems with places we are already using this.
   *
   * NOTE: Needs a refactor
   * This can have problems if this is a field that receives data from an endpoint for the initial content
   * On first render it receives "" for initial content and doesn't update when it receives the saved value
   * useEditor has a dependency array where you can put the value to watch to make it update
   * If you put initialContent there, it gets into an infinite loop because of the handleUpdate function running when the value changes
   * So we need to
   * 1. update when receiving the saved value from the DB
   * 2. but allow for situations where that isn't necessary and the user is just typing
   *
   * This may need to be managed at the location that FnrInput is being used.
   * One workaround is not displaying the component until we have a value.
   * And example of this is TaxInfoScreen. We don't render the input until we get to that screen. By that point we have already gotten the saved value.
   */

  const extensions = newLineOnEnter
    ? [Document, Paragraph, Text]
    : [Document, Paragraph, Text, DisableEnter];

  const editor = useEditor(
    {
      extensions,
      content: initialContent,
      onUpdate({ editor }) {
        let text = editor.getText({ blockSeparator: "/n" });
        handleUpdate(text);
      },
      onBlur({ editor }) {
        if (!onBlur) return;
        onBlur();
      },
      editable: !disabled,
      autofocus: autoFocus,
      DisableEnter,
    },
    []
  );

  return <EditorContent editor={editor} />;
};

const TipTapCE = forwardRef(
  (
    {
      value = "",
      initialContent,
      name = null,
      size = "medium", // "large", "medium", "small"
      onChange = null,
      onBlur = null,
      placeholder,
      disabled,
      autoFocus,
      className,
      backgroundColor = "white",
      textColor = "#222",
      focusColor = "#278dc1",
      // disableMultiline = true,
      newLineOnEnter = false,
      editorMinHeight = null,
      inputType, // textarea | input : used to place the placeholder in it's container
    },
    ref // I'm 98% sure we don't need the ref. That was only needed in the original InputCE
  ) => {
    const [text, setText] = React.useState();

    const handleUpdate = (text) => {
      setText(text);

      if (onChange) {
        // The original InputCE also passes the name here.
        // But there is no place in all of FN that passing it does anything.
        // So I removed it.
        // onChange(text, name);
        onChange(text);
      }
    };

    return (
      <Wrapper
        size={size}
        disabled={disabled}
        className={className}
        backgroundColor={backgroundColor}
        textColor={textColor}
        // disableMultiline={disableMultiline}
        focusColor={focusColor}
        editorMinHeight={editorMinHeight}
      >
        <AnimatePresence>
          {placeholder && (text?.length || value?.length) && (
            <TopLabel
              initial={{ opacity: 0.4, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              bg={backgroundColor}
              size={size}
            >
              <TopLabelText textColor={textColor}>{placeholder}</TopLabelText>
            </TopLabel>
          )}
        </AnimatePresence>

        <Tiptap
          handleUpdate={handleUpdate}
          disabled={disabled}
          initialContent={initialContent || value}
          autoFocus={autoFocus}
          newLineOnEnter={newLineOnEnter}
          onBlur={onBlur}
        />

        {(text?.length <= 0 || !text) && value?.length <= 0 ? (
          <Placeholder size={size} textColor={textColor} inputType={inputType}>
            {placeholder}
          </Placeholder>
        ) : null}
      </Wrapper>
    );
  }
);

export default TipTapCE;

const getFontSize = (size) => {
  if (typeof size === "number") return size + "px";

  switch (size) {
    case "large":
      return "28px";
    case "medium":
      return "18px";
    case "small":
      return "15px";
    default:
      return "18px";
  }
};

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  box-sizing: border-box;
  display: block;

  .ProseMirror {
    background-color: ${(props) => props.backgroundColor || "white"};
    color: ${(props) => props.textColor || "black"};
    border-radius: 7px;
    border: 1px solid #98a4a5;
    padding: 8px 6px 4px;
    padding-bottom: 4px;
    font-size: ${(props) => getFontSize(props.size)};
    min-height: ${(props) =>
      props.editorMinHeight ? props.editorMinHeight : "auto"};

    p {
      margin: 0;
    }

    &.ProseMirror-focused {
      /* border: 0px solid transparent; */

      &:focus-visible {
        border: ${(props) => `1px solid ${props.focusColor}`};
        outline: none;
      }
    }
  }
`;

const Placeholder = styled.div`
  position: absolute;
  box-sizing: border-box;
  top: ${(props) => (props.inputType === "textarea" ? "0" : "50%")};
  transform: ${(props) =>
    props.inputType === "textarea" ? "none" : "translateY(-50%)"};
  color: ${(props) => props.textColor || "black"};
  padding: 6px;
  padding-bottom: 4px;
  font-size: ${(props) => getFontSize(props.size)};
  opacity: 0.7;
  border: 1px solid transparent;
  pointer-events: none;
`;

const TopLabel = styled(motion.div)`
  position: absolute;
  z-index: 1;
  top: -10px;
  left: 8px;
  background-color: ${({ bg }) => bg};
  padding: 2px 4px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 400;
  line-height: 15px;
  margin-bottom: 2px;
`;

const TopLabelText = styled.span`
  color: ${({ textColor }) => textColor};
  opacity: 0.6;
`;
